'use client';

import { useTranslations } from 'next-intl';
import { Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PermissionGate } from '@/features/auth';
import type { ContainerType } from '../types';

interface ContainerTypeCompactRowProps {
  item: ContainerType;
  onEdit: (item: ContainerType) => void;
  onDelete: (item: ContainerType) => void;
}

export function ContainerTypeCompactRow({
  item,
  onEdit,
  onDelete
}: ContainerTypeCompactRowProps) {
  const t = useTranslations('containerTypes');

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      <div className='min-w-0 flex-1'>
        <span className='font-medium'>{item.name}</span>
      </div>

      <Badge
        variant={item.status === 'active' ? 'default' : 'secondary'}
        className={cn(
          item.status === 'active'
            ? 'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
            : 'bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-200'
        )}
      >
        {item.status === 'active' ? t('statusActive') : t('statusInactive')}
      </Badge>

      <div className='flex shrink-0 items-center gap-1'>
        <PermissionGate permissions={['container_types.edit']}>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onEdit(item)}
          >
            <Pencil className='h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
        <PermissionGate permissions={['container_types.delete']}>
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onDelete(item)}
          >
            <Trash2 className='text-destructive h-3.5 w-3.5' />
          </Button>
        </PermissionGate>
      </div>
    </div>
  );
}
